import React, { useEffect, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import dayjs from 'dayjs';
import { getJobTemplates, validateField } from '../firebase';
import BasicModal from './modal';

const FieldWithTooltip = ({ children, toolTip, hasError, readonlyContent }) => {
  return (
    <>
      <Box sx={{ position: 'relative', mb: 2 }}>
        {children}
        {toolTip && !hasError && (
          <Tooltip title={toolTip} placement="right">
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                right: -40,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {readonlyContent && (
        <Box sx={{ ml: 2, mb: 2, mt: -1 }}>
          {readonlyContent}
        </Box>
      )}
    </>
  );
};

export default function NewJobModal({ open, onClose, onCreate, accountDetails }) {
  const timeZone = 'America/New_York'
  const scheduleOptions = [
    { label: 'Every 5 Minutes', cron: '*/5 * * * *' },
    { label: 'Every Hour', cron: '0 * * * *' },
    { label: 'Daily at Midnight', cron: '0 0 * * *' }
  ];
  
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [jobName, setJobName] = useState('');
  const [account, setAccount] = useState(accountDetails?.accounts?.[0] || '');
  const [configValues, setConfigValues] = useState({});
  const [schedule, setSchedule] = useState('');
  const [validating, setValidating] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [extractedData, setExtractedData] = useState({});

  useEffect(() => {
    let active = true;
    const fetchTemplates = async () => {
      if (!open) return;
      setLoading(true);
      try {
        const list = await getJobTemplates();
        if (!active) return;
        setTemplates(list);
        if (list.length > 0) {
          setTemplateId(list[0].id);
        }
      } catch (e) {
        console.error('Failed to load job templates', e);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchTemplates();
    return () => { active = false; };
  }, [open]);

  const selectedTemplate = templates.find(t => t.id === templateId) || null;

  useEffect(() => {
    if (!selectedTemplate) return;
    const defaults = {};
    (selectedTemplate.configSpec || []).forEach(item => {
      if (item.default !== undefined) defaults[item.key] = item.default;
    });
    setConfigValues(defaults);
  }, [selectedTemplate]);

  const onChangeConfig = (key, value) => {
    setConfigValues(prev => ({ ...prev, [key]: value }));
    
    // Check if this field has validation
    if (!selectedTemplate) return;
    const fieldSpec = (selectedTemplate.configSpec || []).find(item => item.key === key);
    if (!fieldSpec || !fieldSpec.validation) return;
    
    // Debounce validation call
    if (validating[key]) clearTimeout(validating[key]);
    
    if (!value || value.trim() === '') {
      // Clear extracted data when value is empty
      setExtractedData(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      // Clear autopopulated fields when value is empty
      if (fieldSpec.autopopulate) {
        setConfigValues(prev => {
          const updated = { ...prev };
          fieldSpec.autopopulate.forEach(autoKey => {
            updated[autoKey] = '';
          });
          return updated;
        });
      }
      // Clear validation error
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      setValidating(prev => ({ ...prev, [key]: true }));
      try {
        const data = await validateField(fieldSpec.validation, value);
        
        if (data.valid && data.extracted) {
          // Store extracted data to display for validation feedback
          setExtractedData(prev => ({
            ...prev,
            [key]: data.extracted
          }));
          
          // Autopopulate readonly fields if they match the autopopulate list
          if (fieldSpec.autopopulate) {
            setConfigValues(prev => {
              const updated = { ...prev };
              fieldSpec.autopopulate.forEach(autoKey => {
                if (data.extracted[autoKey] !== undefined) {
                  updated[autoKey] = data.extracted[autoKey];
                }
              });
              return updated;
            });
          }
          
          // Clear any previous error
          setValidationErrors(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
          });
        } else if (!data.valid) {
          // Clear extracted data on validation failure
          setExtractedData(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
          });
          // Set validation error
          setValidationErrors(prev => ({
            ...prev,
            [key]: data.message || 'Invalid URL'
          }));
        }
      } catch (error) {
        console.error('Validation failed:', error);
        setExtractedData(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
        setValidationErrors(prev => ({
          ...prev,
          [key]: error.message || 'Validation failed'
        }));
      } finally {
        setValidating(prev => ({ ...prev, [key]: false }));
      }
    }, 500); // 500ms debounce
    
    setValidating(prev => ({ ...prev, [key]: timeoutId }));
  };

  const requiredMissing = (() => {
    if (!selectedTemplate) return true;
    if (!templateId) return true;
    if (!jobName) return true;
    if (!account) return true;
    
    const spec = (selectedTemplate.configSpec || []);
    const configMissing = spec.some((item) => {
      if (!item.required) return false;
      const v = configValues[item.key];
      if (v === undefined || v === null) return true;
      if (typeof v === 'string') return v.trim() === '';
      return false;
    });
    
    if (configMissing) return true;
    
    const reportLinkField = spec.find(item => item.validation === 'st-report-link');
    if (reportLinkField) {
      const extracted = extractedData[reportLinkField.key];
      if (extracted && extracted.parameters) {
        const reportParams = configValues.serviceTitanReportParameters || {};
        const paramsMissing = extracted.parameters.some(param => {
          if (!param.required) return false;
          const v = reportParams[param.key];
          if (v === undefined || v === null) return true;
          if (typeof v === 'string') return v.trim() === '';
          if (Array.isArray(v)) return v.length === 0;
          return false;
        });
        if (paramsMissing) return true;
      }
    }
    
    return false;
  })();

  const resetForm = () => {
    setJobName('');
    setTemplateId(templates.length > 0 ? templates[0].id : '');
    setAccount(accountDetails?.accounts?.[0] || '');
    setConfigValues({});
    setSchedule('');
    setExtractedData({});
    setValidationErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    const selectedSchedule = scheduleOptions.find(opt => opt.label === schedule);
    const payload = {
      jobName,
      templateId,
      account,
      config: configValues,
    };
    if (selectedSchedule) {
      payload.schedule = { schedule: selectedSchedule.cron, timeZone };
    }
    console.log(`Payload: ${JSON.stringify(payload)}`)
    if (onCreate) {
      onCreate(payload);
    }
    resetForm();
    onClose();
  };

  const renderConfigField = (item, isParameter = false) => {
    const key = item.key;
    const displayName = item.displayName || key;
    const required = item.required || false;
    const type = item.type;
    
    let value, onChange;
    
    if (isParameter) {
      const reportParams = configValues.serviceTitanReportParameters || {};
      const isMulti = type === 'multiselect';
      value = reportParams[key] ?? (isMulti ? [] : '');
      onChange = (newValue) => {
        setConfigValues(prev => ({
          ...prev,
          serviceTitanReportParameters: {
            ...(prev.serviceTitanReportParameters || {}),
            [key]: newValue
          }
        }));
      };
    } else {
      const isMulti = type === 'multiselect';
      value = configValues[key] ?? (isMulti ? [] : '');
      onChange = (newValue) => onChangeConfig(key, newValue);
    }
    
    const isValidating = !isParameter && validating[key] === true;
    const isReadonly = item.readonly === true;
    const validationError = !isParameter && validationErrors[key];
    
    // Don't render readonly fields - they'll be shown below their parent field
    if (isReadonly) {
      return null;
    }
    
    if (type === 'enum' || type === 'multiselect') {
      const isMulti = type === 'multiselect';
      return (
        <FieldWithTooltip toolTip={item.toolTip} key={key}>
          <FormControl fullWidth required={required}>
            <InputLabel id={`${key}-label`}>{displayName}</InputLabel>
            <Select
              labelId={`${key}-label`}
              label={displayName}
              value={value}
              multiple={isMulti}
              onChange={(e) => onChange(e.target.value)}
            >
              {(item.enum || item.options || []).map(opt => {
                const optValue = opt.value !== undefined ? opt.value : opt;
                const optLabel = opt.label !== undefined ? opt.label : String(opt);
                return <MenuItem key={optValue} value={optValue}>{optLabel}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </FieldWithTooltip>
      );
    }
    if (type === 'boolean') {
      return (
        <FieldWithTooltip toolTip={item.toolTip} key={key}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={value === true}
                  onChange={(e) => onChange(e.target.checked)}
                />
              }
              label={displayName}
            />
          </FormGroup>
        </FieldWithTooltip>
      );
    }
    if (type === 'daterange') {
      const startValue = configValues[item.startKey] ?? null;
      const endValue = configValues[item.endKey] ?? null;
      
      return (
        <FieldWithTooltip toolTip={item.toolTip} key={key}>
          <DateRangePicker
            startText={`Start`}
            endText={`End`}
            value={[
              startValue ? dayjs.unix(startValue) : null,
              endValue ? dayjs.unix(endValue) : null
            ]}
            onChange={(newValue) => {
              const [start, end] = newValue;
              if (start && start.isValid && start.isValid()) {
                const startTimestamp = Math.floor(start.startOf('day').valueOf() / 1000);
                onChangeConfig(item.startKey, startTimestamp);
              } else if (!start) {
                onChangeConfig(item.startKey, null);
              }
              if (end && end.isValid && end.isValid()) {
                const endTimestamp = Math.floor(end.endOf('day').valueOf() / 1000);
                onChangeConfig(item.endKey, endTimestamp);
              } else if (!end) {
                onChangeConfig(item.endKey, null);
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true
              }
            }}
            sx={{ width: '100%' }}
          />
        </FieldWithTooltip>
      );
    }
    if (type === 'timestamp' || type === 'date') {
      const dateValue = value ? new Date(value * 1000).toISOString().split('T')[0] : '';
      return (
        <FieldWithTooltip toolTip={item.toolTip} key={key}>
          <TextField
            label={displayName}
            type="date"
            required={required}
            value={dateValue}
            onChange={(e) => {
              const dateStr = e.target.value;
              if (dateStr) {
                const timestamp = Math.floor(new Date(dateStr + 'T00:00:00Z').getTime() / 1000);
                onChange(timestamp);
              } else {
                onChange('');
              }
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
            sx={{
              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: 'invert(1)',
              }
            }}
          />
        </FieldWithTooltip>
      );
    }
    if (type === 'number') {
      return (
        <FieldWithTooltip toolTip={item.toolTip} key={key}>
          <TextField
            label={displayName}
            type="number"
            required={required}
            value={value}
            onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : '')}
            fullWidth
          />
        </FieldWithTooltip>
      );
    }
    return (
      <FieldWithTooltip 
        toolTip={item.toolTip} 
        hasError={!!validationError} 
        key={key}
        readonlyContent={!isParameter && item.validation ? renderReadonlyFields(item) : null}
      >
        <TextField
          label={displayName}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isReadonly}
          error={!!validationError}
          helperText={validationError}
          slotProps={{
            input: {
              endAdornment: isValidating ? <CircularProgress size={20} /> : null
            }
          }}
          fullWidth
        />
      </FieldWithTooltip>
    );
  };

  const renderReadonlyFields = (parentItem) => {
    const extracted = extractedData[parentItem.key];
    if (!extracted) return null;
    
    const { parameters, ...otherExtracted } = extracted;
    
    const extractedDisplay = Object.keys(otherExtracted).length > 0 && (
      Object.entries(otherExtracted).map(([key, value]) => {
        const displayName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        
        return (
          <Box key={key} sx={{ mb: 0.5 }}>
            <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {displayName}:
            </Box>
            <Box component="span" sx={{ fontSize: '0.875rem', ml: 1, color: 'text.primary' }}>
              {value}
            </Box>
          </Box>
        );
      })
    );
    
    const dynamicParams = parameters && parameters.map(param => renderConfigField(param, true));
    
    return (
      <>
        {extractedDisplay}
        {dynamicParams && <Box sx={{ mt: 2 }}>{dynamicParams}</Box>}
      </>
    );
  };

  const content = (
    loading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
        <CircularProgress size={24} />
      </Box>
    ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Box sx={{ pr: 5 }}>
          <FieldWithTooltip toolTip="A unique name to identify this job">
            <TextField required label="Job Name" value={jobName} onChange={(e) => setJobName(e.target.value)} fullWidth />
          </FieldWithTooltip>
          <FieldWithTooltip toolTip="The type of job to run">
            <FormControl fullWidth required>
              <InputLabel id="template-select-label">Template</InputLabel>
              <Select
                labelId="template-select-label"
                id="template-select"
                value={templateId}
                label="Template"
                onChange={(e) => setTemplateId(e.target.value)}
              >
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.displayName || t.id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldWithTooltip>
          {accountDetails.accounts.length > 1 && (
            <FieldWithTooltip toolTip="The account to run this job under">
              <FormControl fullWidth required>
                <InputLabel id="account-label">Account</InputLabel>
                <Select labelId="account-label" label="Account" value={account} onChange={(e) => setAccount(e.target.value)}>
                    {accountDetails.accounts.map((acc) => (
                      <MenuItem key={acc} value={acc}>{acc}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            </FieldWithTooltip>
          )}
          <FieldWithTooltip toolTip="Optionally schedule this job to run automatically at regular intervals">
            <FormControl fullWidth>
              <InputLabel id="schedule-label">Schedule</InputLabel>
              <Select labelId="schedule-label" label="Schedule" value={schedule} onChange={(e) => setSchedule(e.target.value)}>
                  <MenuItem value="">None</MenuItem>
                  {scheduleOptions.map((opt) => (
                    <MenuItem key={opt.label} value={opt.label}>{opt.label}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </FieldWithTooltip>
        </Box>
        <Box sx={{ pr: 5 }}>
          {selectedTemplate && (
            <Box sx={{ mb: 2 }}>
              {(selectedTemplate.configSpec || []).map(item => (
                <React.Fragment key={item.key}>
                  {renderConfigField(item)}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    )
  );

  return (
    <BasicModal
      open={open}
      onClose={handleClose}
      title="New Job"
      onSubmit={handleCreate}
      submitLabel="Create"
      disableSubmit={loading || !selectedTemplate || requiredMissing}
      maxWidth="md"
    >
      {content}
    </BasicModal>
  );
}
